// The ONE definition of what a `requests.status` value means to client code.
//
// ── Why this file exists ────────────────────────────────────────────────────
// `cancelled` appeared on requests on 2026-09-20 and the SAME defect was then
// fixed four times in one day, each time scoped to the screen where it was
// noticed. The fifth was the trader's Home feed. The reason it kept recurring
// is that every surface answered "is this request still live?" in its own
// words, so fixing one taught the others nothing.
//
// Golden rule 12: one predicate, one place. A list restated in eight places is
// eight places to forget.
//
// ── The rule these exports encode ───────────────────────────────────────────
// TEST FOR THE STATES YOU ACT ON, NEVER FOR THE ONES YOU EXCLUDE. A negative
// test (`status !== 'cancelled'`, or a bare `else`) silently adopts every state
// invented after it was written — which is exactly how `cancelled` became a
// to-do item on the trader's dashboard without anyone editing that code.
//
// ── The full vocabulary (requests_status_check, verified in production) ─────
//   open · offers_received · closed · supplier_confirmed · supplier_rejected
//   pending_supplier_confirmation · paid · ready_to_ship · shipping · arrived
//   delivered · cancelled
// Twelve. Any predicate here that does not account for all twelve is wrong for
// whichever ones it forgot.

const norm = (status) => String(status || '').trim().toLowerCase();

/**
 * The cancelled status, by name.
 *
 * Moved here from DashboardBuyer.jsx (where it was `const CANCELLED` at the top
 * of the file) so the trader's requests list and the trader's Home feed answer
 * this question from the same line rather than two. DashboardBuyer imports it
 * from here now; there is no second copy.
 */
export const CANCELLED = 'cancelled';

/**
 * Is this request cancelled?
 *
 * ⚠️ This IS a negative test in spirit — it names the dead state rather than
 * the live ones — and that is a deliberate, owner-approved choice (2026-09-20).
 * The trader's feed legitimately spans nearly the whole lifecycle, so there is
 * no short positive list of "live" the way there is for `isRequestOfferable`
 * (requestDistribution.js), where only two of twelve states qualify.
 *
 * What keeps that safe is that it is used as an EARLY DROP, never as the only
 * classifier: the branches downstream still name the states they act on. If a
 * second dead status is ever added (`abandoned`, `expired`, …) it must be added
 * HERE, and this comment is the reason someone will find this line when they
 * look.
 */
export const isRequestCancelled = (status) => norm(status) === CANCELLED;

/**
 * Statuses in which a standard RFQ is still gathering supplier offers — i.e.
 * the states the "still collecting offers" branch ACTS ON.
 *
 * This replaced a bare `else`. The `else` was the bug: `cancelled` is not
 * 'delivered' and not an active order, so it fell through into "still
 * collecting offers", and a cancelled request with a pending visible offer
 * became "قارن العروض ←" in the trader's needs-action list.
 *
 * The two odd members, named rather than dropped so that behaviour for every
 * CURRENTLY KNOWN status is byte-for-byte what it was before this list existed:
 *   · pending_supplier_confirmation — a direct-purchase status. A request with
 *     a product_ref is handled by the direct branch before reaching here, so
 *     this is believed unreachable on the RFQ path. Named anyway: "believed
 *     unreachable" is not a reason to change what a live row does.
 *   · supplier_rejected — today this renders as though the RFQ were still
 *     collecting. That is preserved here deliberately. Whether it SHOULD is a
 *     product question, not a side effect of this fix, and it needs the owner.
 */
export const RFQ_COLLECTING = [
  'open',
  'offers_received',
  'pending_supplier_confirmation',
  'supplier_rejected',
];

export const isRfqCollecting = (status) => RFQ_COLLECTING.includes(norm(status));

/**
 * What each `requests.status` value is CALLED, in the reader's language.
 *
 * ── Why it lives here and not on a page ────────────────────────────────────
 * It was `STATUS_AR` / `STATUS_EN`, two object literals at the top of
 * DashboardBuyer.jsx. Nothing else could reach them, so the trader's request
 * page (RequestDetail.jsx, the one the email link opens) rendered the raw
 * column instead — «الحالة: offers_received» to a trader who has never heard
 * of that string. The vocabulary was already documented in this file; the
 * labels for it belonged here too.
 *
 * ── zh is NEW, and that is the point ───────────────────────────────────────
 * The old pair was consulted as `isAr ? STATUS_AR : STATUS_EN`, so a Chinese
 * trader read English. There is no "the dashboard's trilingual status map" to
 * reuse — there was a bilingual one — and this is it becoming trilingual.
 *
 * ── All twelve, deliberately ───────────────────────────────────────────────
 * The vocabulary above lists twelve and this map has twelve. A missing key
 * falls back to the raw value, which is the defect this exists to remove, so
 * completeness is the whole job. src/lib/requestStatus.test.js fails if the
 * three languages ever stop agreeing on which keys exist.
 */
export const REQUEST_STATUS_LABELS = {
  ar: {
    open: 'مرفوع',
    offers_received: 'عروض وصلت',
    closed: 'تم قبول عرض',
    pending_supplier_confirmation: 'بانتظار تأكيد المورد',
    supplier_confirmed: 'المورد جاهز',
    supplier_rejected: 'رفض المورد الطلب',
    paid: 'تم الدفع',
    ready_to_ship: 'الشحنة جاهزة',
    shipping: 'قيد الشحن',
    arrived: 'وصل السعودية',
    delivered: 'تم التسليم',
    cancelled: 'ملغي',
  },
  en: {
    open: 'Posted',
    offers_received: 'Offers In',
    closed: 'Offer accepted',
    pending_supplier_confirmation: 'Awaiting Supplier',
    supplier_confirmed: 'Supplier Ready',
    supplier_rejected: 'Supplier Declined',
    paid: 'Paid',
    ready_to_ship: 'Ready to Ship',
    shipping: 'Shipping',
    arrived: 'Arrived',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  },
  zh: {
    open: '已发布',
    offers_received: '已收到报价',
    closed: '已接受报价',
    pending_supplier_confirmation: '等待供应商确认',
    supplier_confirmed: '供应商已确认',
    supplier_rejected: '供应商已拒绝',
    paid: '已付款',
    ready_to_ship: '待发货',
    shipping: '运输中',
    arrived: '已到达',
    delivered: '已交付',
    cancelled: '已取消',
  },
};

/**
 * The label for one status, in one language.
 *
 * Falls back to the RAW VALUE rather than to a guess. A status this map has
 * never heard of is a new status somebody added to the database, and showing
 * it verbatim is ugly in exactly the way that gets it noticed and fixed —
 * whereas defaulting to 'open' (which the old call site at DashboardBuyer:94
 * did) tells the trader their cancelled request is live. Loud and wrong beats
 * quiet and wrong on a status line.
 */
export const requestStatusLabel = (status, lang) => {
  const key = norm(status);
  if (!key) return '';
  const pack = REQUEST_STATUS_LABELS[lang] || REQUEST_STATUS_LABELS.ar;
  return pack[key] || REQUEST_STATUS_LABELS.ar[key] || status;
};


/**
 * What COLOUR a status should be, as a token name rather than a colour.
 *
 * ── Why this is here and not in the badge component ────────────────────────
 * `closed` read «مغلق» in grey on the console, which sounds like a dead end.
 * It is the opposite: it is the moment the trader ACCEPTED an offer, the
 * single most positive event in a request's life. The word is fixed in the map
 * above; the colour is fixed here, because a label that says "accepted" inside
 * a grey badge still reads as an ending.
 *
 * ── Token names, never colours ─────────────────────────────────────────────
 * The values are the EXISTING admin-console badge classes from console.css
 * (.ac-badge.ok / .warn / .danger / .neutral / .info). No new colour is
 * introduced by this file and none should be: a hex here would be a second
 * palette, drifting from the first the moment either is touched.
 *
 * ── Everything except `closed` keeps the colour it already had ─────────────
 * `cancelled` was already red at every call site and stays red. The other ten
 * were neutral and stay neutral. This map exists to move ONE status, and it
 * enumerates all twelve only so requestStatus.test.js can prove none was
 * forgotten — the same reason REQUEST_STATUS_LABELS lists all twelve.
 *
 * ⚠️ NOT a general "status colour" service. It answers for `requests.status`
 * and nothing else. AdminStatusBadge's map covers disputes, support tickets,
 * concierge threads, factories and suppliers, where `closed` means what it
 * says; do not point that component here.
 */
export const REQUEST_STATUS_TONES = {
  open: 'neutral',
  offers_received: 'neutral',
  closed: 'ok',
  pending_supplier_confirmation: 'neutral',
  supplier_confirmed: 'neutral',
  supplier_rejected: 'neutral',
  paid: 'neutral',
  ready_to_ship: 'neutral',
  shipping: 'neutral',
  arrived: 'neutral',
  delivered: 'neutral',
  cancelled: 'danger',
};

/**
 * The badge token for one status.
 *
 * Falls back to 'neutral' rather than to a guess, and that fallback is the
 * conservative direction: an unknown status painted grey is unremarkable,
 * whereas one painted green would announce a success nobody defined. Compare
 * requestStatusLabel, which falls back LOUDLY to the raw value — a wrong word
 * should be noticed, a neutral colour should not shout.
 */
export const requestStatusTone = (status) =>
  REQUEST_STATUS_TONES[norm(status)] || 'neutral';


/**
 * Statuses in which a DEAL EXISTS — the trader accepted an offer and the
 * request moved on into fulfilment.
 *
 * Named as a list of what it IS, never as "everything after closed", because a
 * range test silently adopts any status invented later (golden rule 12). All
 * seven are enumerated and requestStatus.test.js proves each is a real status.
 */
export const REQUEST_DEAL_STATUSES = [
  'closed',
  'supplier_confirmed',
  'paid',
  'ready_to_ship',
  'shipping',
  'arrived',
  'delivered',
];

/**
 * WHY a request cannot be distributed to factories, in the admin's words.
 *
 * ── The bug ────────────────────────────────────────────────────────────────
 * DistributePanel printed «هذا الطلب ملغي — لا يمكن توزيعه» for EVERY
 * blocked status, so a request whose trader had just ACCEPTED an offer was
 * reported to the admin as cancelled. Two opposite outcomes, one sentence.
 *
 * ── Three answers, chosen by naming states, not by excluding them ──────────
 *   cancelled                → the trader walked away
 *   a DEAL status            → an offer was accepted
 *   anything else that is    → it is simply no longer collecting
 *   not collecting
 *
 * ⚠️ THIS DOES NOT DECIDE WHETHER DISTRIBUTION IS ALLOWED. It only explains a
 * refusal that has already been decided elsewhere — by `offerable`, computed
 * in each screen's own data layer from the RAW requests.status, and enforced
 * again server-side by request_is_offerable() inside
 * admin_create_request_invites and admin_route_request_to_suppliers. Do not
 * let a call site infer permission from this function: it answers "why", and
 * a "why" that becomes a "whether" is how the 2026-09-20 incident happened.
 */
export const requestDistributionBlockReason = (status, lang) => {
  const key = norm(status);
  const ar = lang !== 'en';
  if (key === CANCELLED) {
    return ar
      ? 'هذا الطلب ملغي — لا يمكن توزيعه'
      : 'This request is cancelled — it cannot be distributed';
  }
  if (REQUEST_DEAL_STATUSES.includes(key)) {
    return ar
      ? 'قُبل عرض على هذا الطلب — لا يمكن توزيعه'
      : 'An offer was accepted on this request — it cannot be distributed';
  }
  return ar
    ? 'هذا الطلب لم يعد يستقبل عروضاً'
    : 'This request is no longer receiving offers';
};
